import { CompetencyMapQuery } from "@generated/graphql";
import { Divider, Drawer, List, ListItem, ListItemButton, ListItemText, Tooltip } from "@mui/material";
import {
  COMPETENCIES_IDS_PARAM_NAME,
  Competency_Map_Tabs_Values,
  KNOWLEDGE_BLOCKS_IDS_PARAM_NAME,
} from "constants/global";
import useTab from "hooks/useTab";
import { useSearchParams } from "react-router-dom";
import qs from "qs";
import { Fragment, useMemo } from "react";
import { getIdsSearchParam } from "../helpers";

interface NavigationSidebarProps {
  open: boolean;
  handleClose: () => void;
  competencyMap: CompetencyMapQuery["competencyMap"];
}

const NavigationSidebar = ({ open, handleClose, competencyMap }: NavigationSidebarProps) => {
  const tab = useTab<Competency_Map_Tabs_Values>();
  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => qs.parse(searchParams.toString()), [searchParams]) as Record<string, string>;

  const handleListItemClick = ({
    competencyId,
    knowledgeBlockId,
    questionId,
  }: {
    competencyId: string;
    knowledgeBlockId?: string;
    questionId?: string | null;
  }) => {
    if (tab === Competency_Map_Tabs_Values.constructor) {
      const competentiesIdsParamValue = getIdsSearchParam(
        competencyId,
        params[COMPETENCIES_IDS_PARAM_NAME],
        false
      ).join(",");

      const knowledgeBlocksIdsParamValue = knowledgeBlockId
        ? getIdsSearchParam(knowledgeBlockId, params[KNOWLEDGE_BLOCKS_IDS_PARAM_NAME], false).join(",")
        : null;

      let paramsWithIdsParam: Record<string, string> = {
        ...params,
        [COMPETENCIES_IDS_PARAM_NAME]: competentiesIdsParamValue,
      };

      if (knowledgeBlocksIdsParamValue) {
        paramsWithIdsParam = { ...paramsWithIdsParam, [KNOWLEDGE_BLOCKS_IDS_PARAM_NAME]: knowledgeBlocksIdsParamValue };
      }

      setSearchParams(qs.stringify({ ...paramsWithIdsParam, scrollIntoViewId: competencyId }), { replace: true });
    } else if (tab === Competency_Map_Tabs_Values.questionsList) {
      setSearchParams(qs.stringify({ ...params, scrollIntoViewId: questionId }), { replace: true });
    }

    handleClose();
  };

  const competences = competencyMap?.competences;

  return (
    <Drawer
      open={open}
      onClose={handleClose}>
      <List sx={{ minWidth: 300, maxWidth: 500 }}>
        {competences?.map((competency, index) => {
          let competencyFirstQuestionId: string | null = null;
          const competencyDisabled =
            tab === Competency_Map_Tabs_Values.questionsList &&
            competency.knowledgeBlockCompetences?.every(({ knowledgeBlock }) =>
              knowledgeBlock.skills?.every((skill) => !skill.questions?.length)
            );

          return (
            <Fragment key={competency.id}>
              <ListItem sx={{ flexDirection: "column", alignItems: "flex-start", pb: 0 }}>
                <Tooltip title={competencyDisabled ? "Отсутствуют вопросы" : ""}>
                  <span>
                    <ListItemButton
                      disabled={competencyDisabled}
                      onClick={() =>
                        handleListItemClick({ competencyId: competency.id, questionId: competencyFirstQuestionId })
                      }
                      sx={{ py: 0, px: 0 }}>
                      <ListItemText
                        primary={competency.title}
                        sx={{ "& .MuiTypography-root": { fontWeight: 700 } }}
                      />
                    </ListItemButton>
                  </span>
                </Tooltip>
                <List sx={{ pt: 0, pb: 0 }}>
                  {competency.knowledgeBlockCompetences?.map(({ knowledgeBlock }) => {
                    let knowledgeBlockFirstQuestionId: string | null = null;
                    const knowledgeBlockDisabled =
                      tab === Competency_Map_Tabs_Values.questionsList &&
                      knowledgeBlock.skills?.every((skill) => !skill.questions?.length);

                    return (
                      <ListItem
                        key={`${competency.id}-${knowledgeBlock.id}`}
                        sx={{ flexDirection: "column", alignItems: "flex-start", py: 0 }}>
                        <Tooltip title={knowledgeBlockDisabled ? "Отсутствуют вопросы" : ""}>
                          <span>
                            <ListItemButton
                              disabled={knowledgeBlockDisabled}
                              onClick={() =>
                                handleListItemClick({
                                  competencyId: competency.id,
                                  knowledgeBlockId: knowledgeBlock.id,
                                  questionId: knowledgeBlockFirstQuestionId,
                                })
                              }
                              sx={{ py: 0, px: 0 }}>
                              <ListItemText
                                primary={knowledgeBlock.title}
                                sx={{ "& .MuiTypography-root": { fontWeight: 500 } }}
                              />
                            </ListItemButton>
                          </span>
                        </Tooltip>
                        <List sx={{ pt: 0 }}>
                          {knowledgeBlock.skills?.map((skill) => {
                            const skillFirstQuestion = skill.questions?.[0];
                            if (skillFirstQuestion) {
                              if (!knowledgeBlockFirstQuestionId) knowledgeBlockFirstQuestionId = skillFirstQuestion.id;
                              if (!competencyFirstQuestionId) competencyFirstQuestionId = skillFirstQuestion.id;
                            }

                            const skillDisabled =
                              tab === Competency_Map_Tabs_Values.questionsList && !skillFirstQuestion;

                            return (
                              <ListItem
                                key={`${competency.id}-${knowledgeBlock.id}-${skill.id}`}
                                sx={{ flexDirection: "column", alignItems: "flex-start", py: 0 }}>
                                <Tooltip title={skillDisabled ? "Отсутствуют вопросы" : ""}>
                                  <span>
                                    <ListItemButton
                                      disabled={skillDisabled}
                                      onClick={() =>
                                        handleListItemClick({
                                          competencyId: competency.id,
                                          knowledgeBlockId: knowledgeBlock.id,
                                          questionId: skillFirstQuestion?.id,
                                        })
                                      }
                                      sx={{ py: 0, px: 0 }}>
                                      <ListItemText primary={skill.title} />
                                    </ListItemButton>
                                  </span>
                                </Tooltip>
                              </ListItem>
                            );
                          })}
                        </List>
                      </ListItem>
                    );
                  })}
                </List>
              </ListItem>
              {index !== competences.length - 1 && <Divider />}
            </Fragment>
          );
        })}
      </List>
    </Drawer>
  );
};

export default NavigationSidebar;
